import clsx from 'clsx';
import type { FunctionComponent } from 'react';
import { useCallback, useRef } from 'react';

import { RichEditor } from '~/ui/core/rich-editor/RichEditor';

import { reportRecoveredRichEditorError } from '../../../data-dog';
import { RichEditorToolbarWithTranscribe } from '../../job-profile-section/RichEditorToolbarWithTranscribe';
import { toProfileTaskRichTextHtml } from '../profile-task-rich-text';

export const ProfileTaskRichTextField: FunctionComponent<{
    readonly compact?: boolean;
    readonly field: string;
    readonly label: string;
    /** Keeps the label as the accessible name of the field group while hiding it visually. */
    readonly labelHidden?: boolean;
    readonly onChange: (value: string) => void;
    readonly taskId: string;
    readonly testId?: string;
    readonly value: string | undefined;
}> = ({ compact = false, field, label, labelHidden = false, onChange, taskId, testId, value }) => {
    const handleEditorError = useCallback(
        (error: Error) => {
            reportRecoveredRichEditorError({ field, taskId }, error);
        },
        [field, taskId],
    );
    // Feeding the editor's own output back through the sanitizer changes the markup and
    // makes the editor rebuild its content, which resets the caret. Pass values the editor
    // emitted itself through unchanged; only sanitize values arriving from elsewhere.
    const lastEmittedHtml = useRef<string>(undefined);
    const handleChange = useCallback(
        (html: string) => {
            lastEmittedHtml.current = html;
            onChange(html);
        },
        [onChange],
    );
    const editorValue =
        value !== undefined && value === lastEmittedHtml.current ? value : toProfileTaskRichTextHtml(value);

    return (
        <fieldset
            className="m-0 flex flex-col border-0 p-0 text-body-s font-medium leading-normal text-text-primary"
            data-testid={testId}
        >
            <legend className={clsx('p-0', labelHidden ? 'sr-only' : 'mb-s')}>{label}</legend>
            <div className="overflow-hidden rounded-sm border border-border-control">
                <RichEditor
                    changeDebounceMs={0}
                    className={clsx(
                        '[&>div:last-of-type]:min-h-32 [&_[contenteditable]]:min-h-24',
                        compact && '[&>div:last-of-type]:min-h-24 [&_[contenteditable]]:min-h-16',
                    )}
                    toolbar={<RichEditorToolbarWithTranscribe />}
                    value={editorValue}
                    onChange={handleChange}
                    onError={handleEditorError}
                />
            </div>
        </fieldset>
    );
};
